package com.arenaproplay.admin.ui.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.state.auth.LocalAuthSession
import com.arenaproplay.admin.state.shell.rememberShellCounts
import com.arenaproplay.admin.ui.components.admin.PageScene
import com.arenaproplay.admin.ui.components.overlay.CommandPalette
import com.arenaproplay.admin.ui.layouts.shell.MobileNavDrawer
import com.arenaproplay.admin.ui.layouts.shell.Sidebar
import com.arenaproplay.admin.ui.layouts.shell.Topbar
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyLMedium
import com.arenaproplay.admin.ui.utils.sPadding
import com.arenaproplay.admin.utils.PageSeo
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.w3c.dom.events.KeyboardEvent

@Composable
fun AdminLayout(
    title: String,
    subtitle: String? = null,
    requireAuth: Boolean = true,
    actions: (@Composable () -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    val ctx = rememberPageContext()
    val session = LocalAuthSession.current

    PageSeo(title = "Arena Admin · $title")

    LaunchedEffect(session.isAuthenticated, session.isAdmin) {
        if (requireAuth && (!session.isAuthenticated || !session.isAdmin)) {
            ctx.router.navigateTo(Routes.LOGIN)
        }
    }

    if (requireAuth && (!session.isAuthenticated || !session.isAdmin)) {
        Box(
            Modifier.fillMaxSize().backgroundColor(AppColors.Background),
            contentAlignment = Alignment.Center
        ) {
            SpanText("Redirecting…", Modifier.bodyLMedium().color(AppColors.Text.Secondary))
        }
        return
    }

    val container = LocalAppContainer.current
    // Rail is always 72 px; sidebarOpen kept for compat but not used to toggle width.
    var sidebarOpen by container.sidebarOpen

    // ── Mobile nav drawer state ───────────────────────────────────────────────
    var mobileNavOpen by remember { mutableStateOf(false) }

    // ── Shell counts (online users, support unread) ───────────────────────────
    val shellCounts = rememberShellCounts(container.adminRepository)

    // ── ⌘K / Ctrl+K command palette state ────────────────────────────────────
    var commandPaletteOpen by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        val handler: (dynamic) -> Unit = { e ->
            val ke = e as KeyboardEvent
            val isMacCmd = ke.metaKey as Boolean
            val isCtrl = ke.ctrlKey as Boolean
            if ((isMacCmd || isCtrl) && ke.key == "k") {
                // Do not steal ⌘K from text inputs / editable elements
                val active = document.activeElement
                val tag = active?.tagName?.uppercase() ?: ""
                val isEditable = tag == "INPUT" || tag == "TEXTAREA" ||
                    active?.getAttribute("contenteditable")?.let { it != "false" } == true
                if (!isEditable) {
                    ke.preventDefault()
                    commandPaletteOpen = !commandPaletteOpen
                }
            }
        }
        document.addEventListener("keydown", handler)
        onDispose { document.removeEventListener("keydown", handler) }
    }

    // ── Layout ────────────────────────────────────────────────────────────────
    // na-shell: 100vh с dvh-переопределением — на мобиле 100vh включает зону за
    // адресной строкой браузера, из-за чего низ страницы был недостижим скроллом.
    Box(Modifier.fillMaxWidth().classNames("na-shell").styleModifier { property("position", "relative") }) {

        PageScene(Modifier.fillMaxWidth().height(100.percent)) {

        Row(
            Modifier
                .fillMaxWidth()
                .height(100.percent)
        ) {
            Sidebar(
                isOpen = sidebarOpen,
                onToggle = { container.setSidebarOpen(!sidebarOpen) },
                counts = shellCounts,
                onSearchClick = { commandPaletteOpen = true }
            )

            Column(
                Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .overflow(Overflow.Hidden)
                    .classNames("na-main")
            ) {
                Topbar(
                    title = title,
                    subtitle = subtitle,
                    onToggleSidebar = { container.setSidebarOpen(!sidebarOpen) },
                    onHamburger = { mobileNavOpen = true },
                    actions = actions
                )

                Box(
                    Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .classNames("na-scrollport")
                        .overflow(Overflow.Auto)
                        .styleModifier {
                            property("scrollbar-gutter", "stable")
                            property("-webkit-overflow-scrolling", "touch")
                        }
                ) {
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .classNames("na-page-content")
                            .styleModifier {
                                // Night Arena R3: max-width 1352px per spec (rail is 72px fixed)
                                property("max-width", "1352px")
                                property("margin-left", "auto")
                                property("margin-right", "auto")
                            }
                            .sPadding(28, 32)
                    ) {
                        content()
                    }
                }
            }
        }

        }

        // ── Mobile nav drawer ───────────────────────────────────────────────
        MobileNavDrawer(
            open   = mobileNavOpen,
            onClose = { mobileNavOpen = false },
            counts = shellCounts,
        )

        // ── Command palette overlay ─────────────────────────────────────────
        CommandPalette(
            isOpen = commandPaletteOpen,
            onClose = { commandPaletteOpen = false }
        )
    }
}
